import React from 'react';
import pt from 'prop-types';

import CONSENT_SOURCE_TYPES from './CONSENT_SOURCE_TYPES';
import CMP_PRESETS, { CMP_PRESET_LABELS } from './AnalyticsBuilderConsentSource/CMP_PRESETS';

const SOURCE_LABELS = {
  [CONSENT_SOURCE_TYPES.COOKIE]: 'Cookie',
  [CONSENT_SOURCE_TYPES.JS_API]: 'JavaScript API',
  [CONSENT_SOURCE_TYPES.CALLBACK_JS_API]: 'Callback JavaScript API (saved configuration)',
};

/**
 * The consent rows of the post-build results summary.
 *
 * A jsApi build carries a placeholder in the legacy privacy block, so showing
 * that block's cookieName told those customers their consent came from a
 * cookie they never configured, while the settings that actually drive their
 * consent appeared nowhere. Each row is therefore read from the consent block
 * and shown only when it applies to the configured source.
 *
 * @param {object} params - Parameters
 * @param {object} params.consent - The built syncInjectorSettings.consent block
 * @returns {React.ReactNode} Rows describing the configured consent source
 */
export default function ConsentSourceResultRows({ consent, privacyCookieName }) {
  const consentSource = consent?.consentSource;

  // A legacy config has no consentSource; its cookie name is the only thing
  // there is to report.
  if (!consentSource) {
    return (
      <tr>
        <td>Cookie Name</td>
        <td>{consent?.cookieName ?? privacyCookieName}</td>
      </tr>
    );
  }

  return (
    <>
      <tr>
        <td>Consent Source</td>
        <td>
          {consentSource.type === CONSENT_SOURCE_TYPES.CMP
            ? CMP_PRESET_LABELS[consentSource.provider] ?? consentSource.provider
            : SOURCE_LABELS[consentSource.type] ?? consentSource.type}
        </td>
      </tr>
      {consentSource.type === CONSENT_SOURCE_TYPES.CMP && consentSource.consentCookieName && (
        <tr>
          <td>OneTrust Consent Cookie Name</td>
          <td>{consentSource.consentCookieName}</td>
        </tr>
      )}
      {consentSource.type === CONSENT_SOURCE_TYPES.COOKIE && (
        <>
          <tr>
            <td>Cookie Name</td>
            <td>{consentSource.cookieName}</td>
          </tr>
          <tr>
            <td>Cookie Encoding</td>
            <td>{consentSource.encoding}</td>
          </tr>
        </>
      )}
      {consentSource.type === CONSENT_SOURCE_TYPES.JS_API && (
        <>
          <tr>
            <td>Global Object</td>
            <td>{consentSource.globalObject}</td>
          </tr>
          <tr>
            <td>Method</td>
            <td>{consentSource.method}</td>
          </tr>
          {consentSource.propertyPath && (
            <tr>
              <td>Property Path</td>
              <td>{consentSource.propertyPath}</td>
            </tr>
          )}
        </>
      )}
      {consent.optInValues &&
        !(consentSource.type === CONSENT_SOURCE_TYPES.CMP && consentSource.provider === CMP_PRESETS.ONETRUST) && (
          <tr>
            <td>Opt-In Values</td>
            <td>{consent.optInValues.length ? consent.optInValues.join(', ') : 'None (explicit empty mapping)'}</td>
          </tr>
        )}
      <tr>
        <td>Global Privacy Control</td>
        <td>
          {consent.globalPrivacyControl?.enabled
            ? `Enabled (${consent.globalPrivacyControl.codes.join(', ') || 'no codes selected'})`
            : 'Disabled'}
        </td>
      </tr>
    </>
  );
}

ConsentSourceResultRows.propTypes = {
  consent: pt.object,
  privacyCookieName: pt.string,
};
